import { Booking, Expense, Insight, ServiceType } from '../types';
import { getBusinessSettings } from './businessSettings';
import { logAuditAction } from './auditLogger';

const BACKUP_TIMESTAMP_KEY = 'solostack_last_backup_date';
const BACKUP_SNOOZE_KEY = 'solostack_backup_snoozed_until';
const INSTALLED_AT_KEY = 'solostack_installed_at';

export interface BackupStatus {
  lastBackupDate: Date | null;
  daysSinceLastBackup: number;
  isOverdue: boolean;
  isSnoozed: boolean;
}

/**
 * Returns timestamp of last backup/export/sync in ms, or null if never recorded
 */
export function getLastBackupTimestamp(): number | null {
  const saved = localStorage.getItem(BACKUP_TIMESTAMP_KEY);
  if (!saved) return null;
  const ts = parseInt(saved, 10);
  return isNaN(ts) ? null : ts;
}

/**
 * Records that a backup, export, or sync occurred right now
 */
export function recordBackupAction(): void {
  const now = Date.now();
  localStorage.setItem(BACKUP_TIMESTAMP_KEY, now.toString());
  // Clear snooze state upon successful backup
  localStorage.removeItem(BACKUP_SNOOZE_KEY);
  // Dispatch custom event for real-time reactive UI updates
  window.dispatchEvent(new CustomEvent('solostack_backup_updated', { detail: { timestamp: now } }));
}

/**
 * Snoozes the backup reminder for 24 hours
 */
export function snoozeBackupReminder(): void {
  const snoozeUntil = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
  localStorage.setItem(BACKUP_SNOOZE_KEY, snoozeUntil.toString());
  window.dispatchEvent(new CustomEvent('solostack_backup_updated'));
}

/**
 * Evaluates current workspace backup status against the threshold (default: 7 days)
 */
export function getBackupStatus(thresholdDays: number = 7): BackupStatus {
  const now = Date.now();
  const lastTs = getLastBackupTimestamp();
  const snoozedUntilStr = localStorage.getItem(BACKUP_SNOOZE_KEY);

  const isSnoozed = snoozedUntilStr ? parseInt(snoozedUntilStr, 10) > now : false;

  if (!lastTs) {
    // If never backed up or exported, check install date
    let installTsStr = localStorage.getItem(INSTALLED_AT_KEY);
    if (!installTsStr) {
      installTsStr = now.toString();
      localStorage.setItem(INSTALLED_AT_KEY, installTsStr);
    }
    const installTs = parseInt(installTsStr, 10);
    const daysSinceInstall = Math.max(0, Math.floor((now - installTs) / (1000 * 60 * 60 * 24)));
    
    // Mark overdue if installed > thresholdDays ago and never backed up
    const isOverdue = daysSinceInstall >= thresholdDays;
    return {
      lastBackupDate: null,
      daysSinceLastBackup: daysSinceInstall,
      isOverdue,
      isSnoozed
    };
  }

  const daysSince = Math.max(0, Math.floor((now - lastTs) / (1000 * 60 * 60 * 24)));
  const isOverdue = daysSince >= thresholdDays;

  return {
    lastBackupDate: new Date(lastTs),
    daysSinceLastBackup: daysSince,
    isOverdue,
    isSnoozed
  };
}

/**
 * Instant 1-click JSON Workspace Backup Generator
 */
export function triggerQuickJSONBackup(
  bookings: Booking[],
  expenses: Expense[],
  insights: Insight[],
  streams: ServiceType[]
): void {
  const settings = getBusinessSettings();
  const backupData = {
    appName: 'AzuraOS Executive HQ',
    exportVersion: '1.0',
    exportedAt: new Date().toISOString(),
    businessSettings: settings,
    streams: streams,
    bookings: bookings,
    expenses: expenses,
    insights: insights,
    summary: {
      totalBookingsCount: bookings.length,
      totalExpensesCount: expenses.length,
      totalInsightsCount: insights.length,
      totalStreamsCount: streams.length,
      totalRevenue: bookings.reduce((sum, b) => sum + b.price, 0),
      totalExpenses: expenses.reduce((sum, e) => sum + e.amount, 0)
    }
  };

  const jsonStr = JSON.stringify(backupData, null, 2);
  const dateStr = new Date().toISOString().split('T')[0];
  const filename = `azuraos_full_workspace_backup_${dateStr}.json`;

  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  // Log audit action
  logAuditAction({
    actionType: 'UPDATE_SETTINGS',
    entityType: 'System',
    details: `Triggered 1-Click JSON Workspace Backup (${bookings.length} bookings, ${expenses.length} expenses)`
  });

  // Record backup timestamp & trigger UI refresh
  recordBackupAction();
}
